import { useQuery as useGetRelatedSupplierLinksQuery } from "@faire/web-api/api/seo/brand-related-supplier-pages/brandToken/get-hooks";
import { trackLoggedOutRetailersBrandPageBottomSupplierCarouselClick } from "@faire/web-api/events/loggedOutRetailers/click/brandPageBottomSupplierCarousel";
import { trackLoggedOutRetailersBrandPageBottomSupplierCarouselView } from "@faire/web-api/events/loggedOutRetailers/view/brandPageBottomSupplierCarousel";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { ILayoutElementData } from "@faire/web-api/indigofair/data/ILayoutElementData";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Row } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React, { useMemo } from "react";
import styled from "styled-components";

import { BrandPageLayoutElementRenderer } from "@faire/retailer/views/brand/logged-out/components/__internal__/BrandPageLayoutElementRenderer";
import { Wrapper } from "@faire/retailer/views/brand/logged-out/components/common";

export const BottomSupplierRecommendationCarousel: React.FC<{
  brandToken: string;
}> = ({ brandToken }) => {
  const viewEventTrackingRef = useDoActionWhenVisible(() => {
    trackLoggedOutRetailersBrandPageBottomSupplierCarouselView();
  });

  const { strictLocalize } = useStrictLocalization();

  const { data, error, isLoading } =
    useGetRelatedSupplierLinksQuery(brandToken);

  const suppliersCarouselLayoutElement = useMemo(() => {
    const suppliersLayoutElementData = (data?.supplier_tiles ?? []).map(
      (category) =>
        ILayoutElementData.build({
          category_tile: category,
        })
    );
    if (!suppliersLayoutElementData.length) {
      return undefined;
    }
    return ILayoutElement.build({
      on_click_event_name: "logged_out_brand_page_c3_suppliers_carousel",
      main_text: strictLocalize({
        defaultMessage: "Brands in similar categories",
        description: {
          text: "Title text for bottom suppliers carousel",
        },
      }),
      type: ILayoutElement.Type.AVATAR_CAROUSEL,
      placement: ILayoutElement.Placement.BOTTOM,
      layout_element_data: suppliersLayoutElementData,
    });
  }, [data?.supplier_tiles, strictLocalize]);

  if (isLoading) {
    return (
      <Wrapper>
        <Row justify="center">
          <LoadingSpinner />
        </Row>
        <Spacer size="6x" />
      </Wrapper>
    );
  }

  if (error || !suppliersCarouselLayoutElement) {
    return null;
  }

  return (
    <StyledWrapper ref={viewEventTrackingRef}>
      <BrandPageLayoutElementRenderer
        layoutElement={suppliersCarouselLayoutElement}
        tilesLength={data?.supplier_tiles.length ?? 0}
        onItemClick={
          trackLoggedOutRetailersBrandPageBottomSupplierCarouselClick
        }
      />
      <Spacer size="6x" />
    </StyledWrapper>
  );
};

const StyledWrapper = styled(Wrapper)`
  ${responsiveMediaQueries.tabletAndAbove} {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;
